import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * <YieldCard/>
 * "Earn Yield" — simulated APY MVP. Users can opt-in their Available Balance
 * to earn a projected APY (~4.25%). Earnings accrue lazily on read.
 *
 * Real BaaS partner (Unit / Synctera / Treasury Prime) will replace the
 * simulation once compliance onboarding is complete.
 */
export default function YieldCard() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/yield/overview`, { withCredentials: true });
      setOverview(res.data);
    } catch {
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 60s so users see earnings tick up while they browse
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (nextEnabled) => {
    setToggling(true);
    try {
      const path = nextEnabled ? 'opt-in' : 'opt-out';
      await axios.post(
        `${BACKEND_URL}/api/yield/${path}`,
        { target_type: 'individual', consent_version: 'yield-sim-v1' },
        { withCredentials: true }
      );
      toast.success(nextEnabled ? 'Yield enabled — you\'re earning!' : 'Yield paused');
      await load();
    } catch (e) {
      toast.error('Could not update yield preference');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-neutral-900/70 border-neutral-800">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        </CardContent>
      </Card>
    );
  }

  if (!overview) return null;

  const ind = overview.individual;
  const apy = (ind.apy_pct || 0).toFixed(2);
  const earnings = (ind.accrued_earnings || 0).toFixed(4);
  const projected = (ind.projected_annual || 0).toFixed(2);
  const principal = (ind.current_principal || 0).toFixed(2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        data-testid="yield-card"
        className="relative overflow-hidden border-emerald-500/30 bg-gradient-to-br from-emerald-950/60 via-neutral-900 to-neutral-950 shadow-[0_0_40px_-12px_rgba(16,185,129,0.35)]"
      >
        {/* Neon accent ring */}
        <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-emerald-400/10" />
        <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl" />

        <CardHeader className="relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-white">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                Earn Yield
                <Badge className="ml-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px] tracking-wide">
                  {apy}% APY
                </Badge>
                <Badge variant="outline" className="ml-1 border-amber-500/40 text-amber-300 text-[10px]">
                  SIMULATED
                </Badge>
              </CardTitle>
              <CardDescription className="text-neutral-400 mt-1">
                Grow your Available Balance while it waits between rotations.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-neutral-400">
                {ind.enabled ? 'On' : 'Off'}
              </span>
              <Switch
                data-testid="yield-toggle"
                checked={!!ind.enabled}
                disabled={toggling}
                onCheckedChange={toggle}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="relative space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Available balance" value={`$${principal}`} testId="yield-principal" />
            <Metric
              label="Earnings so far"
              value={`$${earnings}`}
              accent="text-emerald-300"
              testId="yield-earnings"
            />
            <Metric
              label="Projected /yr"
              value={`$${projected}`}
              accent="text-emerald-300"
              testId="yield-projected"
              icon={<TrendingUp className="w-3 h-3 text-emerald-400" />}
            />
          </div>

          {!ind.enabled && (
            <Button
              data-testid="yield-enable-btn"
              onClick={() => toggle(true)}
              disabled={toggling}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-bold"
            >
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : `Enable ${apy}% APY on my balance`}
            </Button>
          )}

          <div className="flex items-start gap-2 text-[11px] text-neutral-500 leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neutral-500" />
            <span>{overview.disclosure}</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function Metric({ label, value, accent = 'text-white', testId, icon }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-neutral-800 bg-neutral-950/60 px-3 py-3"
    >
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`text-lg font-bold ${accent}`}>{value}</div>
    </div>
  );
}
